import React, { useState, useEffect, useMemo, useRef } from "react";
import Table from "./Table";
import useFetchPost from "../Hooks/useFetchPost";
import Loading from "../Ui/Loading";
import UploadFilePopup from "../Popups/UploadFilePopup";
import useSignDocument from "../Hooks/useSignDocument";
import useGetSignatureFile from "../Hooks/useGetSignatureFile";
import useCutFileExtension from "../Hooks/useCutFileExtension";
import Popup from "../Popups/Popup";
import EditUploadedDocument from "../Popups/EditUploadedDocument";
import AccountInfo from "../Profile/AccountInfo";
import usePostRemoveDocuments from "../Api/usePostRemoveDocuments";
import usePostAddSignature from "../Api/usePostAddSignature";

import { IDocumentModel } from "../Common/types";
import { CheckingStatuses, CheckingStatusesDescription } from "../enums/DocCheckingStatuses";
import { DocumentTypes, DocumentTypeTranslations } from "../enums/DocumentTypes";
import { TableMode } from "../enums/TableTypes";
import { useSlot, useSignal } from "react-signal-slot";
import { DocumentCertificateInfoViewModel } from "../Admin/types";

import "../../css/Table.css";

type props = {
    data: IDocumentModel[];
    AllowFileTypes?: string[];
    RequiredTypes?: DocumentTypes[];
    Mode: TableMode;
    MaxSizeFile?: number;
    addSignature?: any;
    withOutSign?: boolean;
    isNoLocalRemove?: boolean;
} & (2 extends TableMode
    ? { removeDocument?: any, onRemove?: (docId: Number | string) => any }
    : { removeDocument: any, onRemove: (docId: Number | string) => any });

const DocumentsTable = ({ data, onRemove, addSignature, isNoLocalRemove, withOutSign, removeDocument, Mode, }: props) => {
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [editedFile, setEditedFile] = useState(null);
    const [isEditFilePopupOpen, setIsEditFilePopupOpen] = useState(false);
    const [docs, setDocs] = useState<IDocumentModel[]>(data);
    const [editedId, setEditedId] = useState(null);

    const [isOpen, setIsOpen] = useState(false);
    const [actionButtons, setActionButtons] = useState([]);

    const { mutate: removeDoc, data: removeDocumentResult, isPending: removeDocumentLoading, isError: removeDocumentIsError, error: removeDocumentError, isSuccess: removeDocumentSuccess } = removeDocument?.() || usePostRemoveDocuments();
    const { mutate: addSign, data: addSignatureResult, isPending: addSignatureLoading, isError: addSignatureIsError, isSuccess: addSignatureSuccess, error: addSignatureError } = addSignature?.() || usePostAddSignature();


    const [certPopupOpen, setCertPopupOpen] = useState(false);
    const [certData, setCertData] = useState<DocumentCertificateInfoViewModel | null>(null);

    const showCertificateInfo = async (docId: number) => {
        try {
            const response = await fetch(`/DocumentApi/GetCertificateInfo?documentId=${docId}`);
            if (!response.ok) throw new Error("Ошибка при получении сертификата");

            const cert: DocumentCertificateInfoViewModel = await response.json();
            setCertData(cert);
            setCertPopupOpen(true);
        } catch (error) {
            console.error("Ошибка загрузки сертификата", error);
            setCertData(null);
            setCertPopupOpen(true);
        }
    };

    const formatDateTime = (dateStr: string): string => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "Неверная дата";
        return date.toLocaleString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };


    useSlot("getWinnerDocument", (doc) => {
        console.log(doc);
        setDocs(prev => ([...(prev || []), doc]));
    })

    // Новый слот для обработки создания протокола
    useSlot("protocolCreated", (doc: IDocumentModel) => {
        console.log("Received protocolCreated:", doc);
        setDocs(prev => {
            // Проверяем, нет ли уже документа с таким ID, чтобы избежать дублирования
            if (!prev.some(existingDoc => existingDoc.Id === doc.Id)) {
                return [...prev, doc];
            }
            return prev;
        });
    });

    useSlot("protocolPartyCreated", (doc: IDocumentModel) => {
    console.log("Received protocolPartyCreated", doc);
    setDocs(prev => {
        // Проверяем, нет ли уже документа с таким ID
        const exists = prev.some(existingDoc => existingDoc.Id === doc.Id);
        if (!exists) {
            return [...prev, doc];
        }
        // Или обновляем существующий
        return prev.map(existingDoc => 
            existingDoc.Id === doc.Id ? {...existingDoc, ...doc} : existingDoc
        );
    });
});

    useEffect(() => { setDocs(data) }, [data]);

    useEffect(() => {
        setLoading(removeDocumentLoading);
        if (removeDocumentIsError) setErrorText(removeDocumentError);
        if (removeDocumentSuccess && editedId) onRemove(editedId);
    }, [removeDocumentLoading, removeDocumentIsError, removeDocumentSuccess]);

    useEffect(() => {
        if (addSignatureIsError) setErrorText(addSignatureError);
        if (addSignatureSuccess && editedId) setDocs(prev => prev.map(doc => doc.Id === editedId ? { ...doc, IsSignatureVendor: true } : doc))
    }, [addSignatureLoading, addSignatureIsError, addSignatureSuccess])

    const renderDocTypeBody = (rowData: IDocumentModel) => {
        return DocumentTypeTranslations[rowData.DocumentTypeId]
    }

    const renderActions = (rowData: IDocumentModel) => {
        return (
            <div className="actions">
                <button className="defaultSmallPinkButton"
                    onClick={() => {
                        setIsOpen(true);

                        setActionButtons([
                            {
                                name: "Да", action: () => {
                                    if (isNoLocalRemove) {
                                        onRemove(rowData.Id);
                                    } else {
                                        setEditedId(rowData.Id)
                                        removeDoc([rowData.Id])
                                    }

                                    setIsOpen(false);
                                }
                            },
                            {
                                name: "Нет", action: () => { setIsOpen(false) }
                            }
                        ])
                    }}
                >Удалить</button>
            </div>
        )
    }

    const renderSignDoc = (rowData: IDocumentModel) => (
        <>
            {rowData.IsSignatureVendor && (
                rowData.OwnerAccountId === AccountInfo.Id ? (
                    <a className="blueLink" style={{ color: "#16bc27" }} href={useGetSignatureFile(rowData.SignedData)} download title="Скачать подпись">
                        Подписан вами
                    </a>
                ) : (
                    <>
                        <a
                            className="blueLink"
                            style={{
                                color: "#16bc27",
                                cursor: "pointer",
                                textDecoration: "underline"
                            }}
                            onClick={() => showCertificateInfo(rowData.Id)}
                        >
                            Подписан организатором
                        </a>

                        {rowData.CanSignCurrentUser && (
                            <a className="blueLink"
                                onClick={() => {
                                    setEditedId(rowData.Id);
                                    addSign(rowData);
                                }}
                            >
                                Добавить свою подпись
                            </a>
                        )}
                    </>
                )
            )}

            {!rowData.IsSignatureVendor && rowData.IsSignatureWinner && (
                <a className="blueLink" href={useGetSignatureFile(rowData.SignedData)} download title="Скачать подпись">
                    Подписан победителем
                </a>
            )}

            {!rowData.IsSignatureVendor && !rowData.IsSignatureWinner && rowData.CanSignCurrentUser && (
                <a className="blueLink"
                    onClick={() => {
                        setEditedId(rowData.Id);
                        addSign(rowData);
                    }}
                >
                    Подписать
                </a>
            )}

            {!rowData.IsSignatureVendor && !rowData.IsSignatureWinner && !rowData.CanSignCurrentUser && (
                <span>Нет данных</span>
            )}
        </>
    );

    const renderDocName = (rowData: IDocumentModel) => (
        <div className="elWrap">
            {rowData.Name}
            {rowData.MimeType &&
                <span className="fileExtension">
                    {rowData.MimeType.includes(".") ? rowData.MimeType.split(".")?.pop() : rowData.MimeType.split("/")?.pop()}
                </span>   
            }
            <a href={rowData.DocumentLink || rowData.Path} download className="downloadDocButton">
                <img src="../../../main-images/downloadDocIcon.png"></img>
            </a>
        </div>
    )

    const renderDocCompany = (rowData: IDocumentModel) => (
        <div className="elWrap">
            {rowData?.AccountName}
        </div>
    )

    const columns = useMemo(() => {
        if (Mode === TableMode.Edit) {
            return [
                { title: "Тип документа", body: renderDocTypeBody, colWidth: "120px" },
                { name: "Name", title: "Название", body: renderDocName, colWidth: "150px" },
                { name: "AccountName", title: "Пользователь", body: renderDocCompany, colWidth: "150px" },
                { name: "Comment", title: "Комментарий", colWidth: "200px" },
                !withOutSign &&
                { name: "Signature", title: "Подпись", body: renderSignDoc, colWidth: "150px" },
                { title: "", body: renderActions, colWidth: "100px", class: "actionsCol" }
            ].filter(i => i);
        } else {
            return [
                { title: "Тип документа", body: renderDocTypeBody, colWidth: "120px" },
                { name: "Name", title: "Название", body: renderDocName, colWidth: "150px" },
                { name: "AccountName", title: "Пользователь", body: renderDocCompany, colWidth: "150px" },
                { name: "Comment", title: "Комментарий", colWidth: "200px" },
                { name: "Signature", title: "Подпись", body: renderSignDoc, colWidth: "150px" },
            ];
        }
    }, [Mode]);

    return (
        <>
            {loading && <Loading />}
            <Table data={docs} columns={columns} />
            <Popup
                isOpen={isOpen}
                close={() => setIsOpen(false)}
                head={{ title: "Вы уверены?" }}
                actionButtons={actionButtons}
            />

            <Popup
                isOpen={certPopupOpen}
                close={() => setCertPopupOpen(false)}
                head={{ title: "Информация о сертификате" }}
            >
                {certData ? (
                    <div>
                        <p><b>Владелец:</b> {certData.Subject}</p>
                        <p><b>Издатель:</b> {certData.Issuer}</p>
                        <p><b>Серийный номер:</b> {certData.SerialNumber}</p>
                        <p><b>Срок действия:</b> с {formatDateTime(certData.ValidFrom)} по {formatDateTime(certData.ValidTo)}</p>
                        <p><b>Алгоритм:</b> {certData.Algorithm}</p>
                        <p><b>Версия:</b> {certData.Version}</p>
                        <p><b>Подтвержден админом:</b> {certData.AdminConfirmed ? "Да" : "Нет"}</p>
                    </div>
                ) : (
                    <p>Ошибка загрузки информации о сертификате</p>
                )}
            </Popup>

        </>
    )
}

export default DocumentsTable;